/**
 * @file Especialização Time do pipeline Input.
 * @module components/create/time
 *
 * Campo de horário reutilizável do EP. Usa a máscara canônica de horário,
 * valida HH:mm e posiciona o ícone à esquerda por meio do prefixo padrão,
 * seguindo o mesmo contrato visual do Date.
 *
 * @see module:components/create/input
 * @see module:components/create/date
 */

import { applyFullDefault } from './fullDefaults';

/**
 * Normaliza valores de horário para HH:mm quando possível.
 *
 * @param {*} value Valor recebido pelo campo.
 * @returns {*} Valor normalizado ou original.
 */
const normalizeTimeValue = (value) => {
  if (value === undefined || value === null || value === '') return value;

  const raw = String(value).trim();
  const direct = raw.match(/^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  if (direct) {
    return `${direct[1]}:${direct[2]}`;
  }

  const iso = raw.match(/T([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?/);
  if (iso) {
    return `${iso[1]}:${iso[2]}`;
  }

  return value;
};

/**
 * Configura um input de horário EP.
 *
 * @param {HTMLInputElement} elem Input base.
 * @param {Object} options Opções recebidas pelo pipeline.
 * @param {string} [options.value] Valor inicial.
 * @param {string} [options.min] Horário mínimo HH:mm.
 * @param {string} [options.max] Horário máximo HH:mm.
 * @returns {Array.<HTMLInputElement|Object>} Input e opções normalizadas.
 */
const time = (elem, options) => {
  applyFullDefault(options);

  options.type = 'text';
  options.value = normalizeTimeValue(options.value);

  if (!options.mask) {
    options.mask = {
      template: 'time',
      placeholder: false,
    };
  }

  if (!options.prefix) {
    options.prefix = {
      icon: {
        name: 'clock',
        type: 'solid',
      },
    };
  }

  const validate = elem.validateTime = () => {
    const value = String(elem.value || '').trim();

    if (!value) {
      delete elem.dataset.valid;
      return;
    }

    const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!match) {
      elem.dataset.valid = 'false';
      return;
    }

    const validMin = !elem.min || value >= elem.min;
    const validMax = !elem.max || value <= elem.max;
    elem.dataset.valid = String(validMin && validMax);
  };

  elem.addEventListener('input', () => {
    if (String(elem.value || '').length === 5) validate();
  });

  elem.addEventListener('blur', validate);

  return [elem, options];
};

export default time;
